<template>
  <div class="ec-line005" :class="{ 'pb-12': isShowButtonLine }">
    <el-breadcrumb separator="/">
      <el-breadcrumb-item>
        <a :href="baseUrl">{{ this.shopName }}</a></el-breadcrumb-item
      >
      <el-breadcrumb-item>{{ $t('ec-line029.title') }}</el-breadcrumb-item>
      <el-breadcrumb-item>
        {{ isCartPeriodic ? $t('ec-line003.tab.subscription') : $t('ec-line003.tab.normal') }}</el-breadcrumb-item
      >
      <el-breadcrumb-item> {{ $t('ec-line005.heading') }}</el-breadcrumb-item>
    </el-breadcrumb>

    <the-heading class="lg:mb-8 mb-5 lg:text-3xl text-xl font-black">
      {{ isCartPeriodic ? $t('ec-line003.tab.subscription') : $t('ec-line003.tab.normal') }}
      <br />
      {{ $t('ec-line005.heading') }}
    </the-heading>

    <the-section>
      <div v-safe-html="fvPart1" class="mt-4"></div>
      <div v-if="liffIdExists && !isRequiredLine && !friendship">
        <div v-safe-html="fvLinePart" class="mt-4"></div>
        <line-guide-step :qr-code="lineLinkUrl" @click-line="onLinkLine"></line-guide-step>
      </div>
      <div v-else>
        {{ $t('ec-line005.thankyouPurchasing') }}
        <br />
        {{ $t('ec-line005.detailsOrder') }}
      </div>
      <div v-safe-html="fvPart2" class=""></div>
    </the-section>

    <purchase-detail-card
      :data="purchase.items"
      :summary="summary"
      :isCartPeriodic="isCartPeriodic"
      :order-id="purchase.order_id"
    ></purchase-detail-card>
    <purchase-information-card :data="memberInfo"></purchase-information-card>
    <shipping-information-card :data="shipping" :business-account-flag="memberInfo.business_account_flag">
    </shipping-information-card>
    <subscription-information-card v-if="isCartPeriodic" :data="subscription"></subscription-information-card>
    <div v-if="isShowButtonLine" class="ec-line005__line-link-button" @click="onLinkLine">
      {{ $t('common.action.lineLink') }}
    </div>
  </div>
</template>

<script>
import mixinRegisterCustomer from '@/mixins/register-customer'
import TheHeading from '@/components/my-page-partials/TheHeading.vue'
import PurchaseInformationCard from '@/components/ec-line005/PurchaseInformationCard.vue'
import ShippingInformationCard from '@/components/ec-line005/ShippingInformationCard.vue'
import SubscriptionInformationCard from '@/components/ec-line005/SubscriptionInformationCard.vue'
import PurchaseDetailCard from '@/components/ec-line005/PurchaseDetailCard.vue'
import LineGuideStep from '@/components/ec-line005/LineGuideStep.vue'
import shopMixin from '@/mixins/shop'
import { SocialService, HtmlService } from '@/services'
import { ENV_KEY } from '@/constants/env'
import { AMAZON } from '@/constants/amazon'
import { AD_USER_ID, ADCD } from '@/constants'
import TheSection from '@/components/my-page-partials/TheSection.vue'
import BreadcrumbMixin from '@/mixins/breadcrumb'
import lineMixin from '@/mixins/line'
import deliveryNoteMixin from '@/mixins/deliveryNote'

export default {
  components: {
    TheHeading,
    PurchaseInformationCard,
    ShippingInformationCard,
    SubscriptionInformationCard,
    PurchaseDetailCard,
    TheSection,
    LineGuideStep,
  },
  mixins: [mixinRegisterCustomer, shopMixin, BreadcrumbMixin, lineMixin, deliveryNoteMixin],
  data() {
    return {
      memberInfo: {},
      shipping: {},
      subscription: {},
      purchase: {
        items: [],
        order_id: '',
      },
      lineLinkUrl: null,
      friendship: false,
      listener: null,
      summary: null,
      fvLinePart: '',
      fvPart1: '',
      fvPart2: '',
    }
  },
  computed: {
    isCartPeriodic() {
      return this.$route.path.includes(this.Constants.CART_TYPE.PERIODIC)
    },
    data() {
      return this.$store.state.register.customer
    },
    saleType() {
      return this.isCartPeriodic ? this.Constants.CART_TYPES.SUBSCRIPTION : this.Constants.CART_TYPES.NORMAL
    },
  },

  async created() {
    await this.checkFriendShip()

    if (this.liffIdExists && !this.isRequiredLine) {
      this.getUrlQrCode()
    }
    this.initMemberInfo()
    this.initShippingInfo()
    this.initSubscription()
    this.initOrder()
    this.fetchHtmlContent()
    this.resetInfoRegister()
  },

  mounted() {
    this.resizeIFrameToFitContent()
  },
  beforeDestroy() {
    this.$session[AMAZON.CART_LINE029] = null
    this.$session[AMAZON.SHIPPING_FEE] = null
  },
  methods: {
    resizeIFrameToFitContent() {
      let iframe = this.$refs.iFrame

      if (!iframe) return
      iframe.onload = () => {
        iframe.removeAttribute('style')
        iframe.style.height = 'calc(' + iframe.contentWindow.document.body.scrollHeight + 'px )'
      }
    },
    /**
     * Init member information
     */
    async initMemberInfo() {
      const { memberInformation } = this.data
      this.memberInfo = {
        name01: memberInformation.lastName,
        name02: memberInformation.firstName,
        kana01: memberInformation.lastNameKana,
        kana02: memberInformation.firstNameKana,
        year: memberInformation.birthdayYear,
        month: memberInformation.birthdayMonth,
        day: memberInformation.birthdayDay,
        gender: this.$store.state.master.sex?.find((item) => item.id === memberInformation.sex)?.name,
        email: memberInformation.email,
        email_confirm: memberInformation.emailConfirm,
        phone_number: memberInformation.phoneNumber,
        postal_code01: memberInformation.postalCode1,
        postal_code02: memberInformation.postalCode2,
        prefecture: this.$store.state.master.pref?.find((item) => item.id === memberInformation.prefecture)?.name,
        addr01: memberInformation.municipality,
        addr02: memberInformation.address,
        addr03: memberInformation.buildingApartmentName,
        company_name: memberInformation.companyName,
        business_account_flag: memberInformation.businessAccountFlag,
      }
    },

    /**
     * Init shipping information
     */
    initShippingInfo() {
      const { shippingInformation } = this.data
      this.shipping = {
        delivery_destinations_text: this.deliveryDestinations.find(
          (item) => item.value === shippingInformation.deliveryDestination
        )?.label,
        name01: shippingInformation.lastName,
        name02: shippingInformation.firstName,
        kana01: shippingInformation.lastNameKana,
        kana02: shippingInformation.firstNameKana,
        postal_code01: shippingInformation.postalCode1,
        postal_code02: shippingInformation.postalCode2,
        prefecture: this.$store.state.master.pref?.find((item) => item.id === shippingInformation.prefecture)?.name,
        addr01: shippingInformation.municipality,
        addr02: shippingInformation.address,
        addr03: shippingInformation.buildingApartmentName,
        phone_number: shippingInformation.phoneNumber,
        shipping_method: this.$store.state.register.shippingMethods?.find(
          (item) => item.id === shippingInformation.shippingMethod
        )?.service_name,
        shipping_address: this.deliveryDestinations?.find(
          (item) => item.value === shippingInformation.deliveryDestination
        )?.label,
        delivery_date: this.Utils.date.formatDateJP(shippingInformation.desiredDeliveryDay),
        delivery_time:
          this.$store.state.register.desiredDeliveryTimeZone?.find(
            (item) => item.value === shippingInformation.desiredDeliveryTimeZone
          )?.label || this.$t('common.placeholder.unavailableTimeZone'),
        delivery_note_text: this.deliveryNotes.find((item) => item.value === shippingInformation.delivery_note_id)
          ?.label,
        bundled_flag: shippingInformation.bundled_flag ? this.$t('common.action.canBe') : this.$t('common.action.none'),
      }
    },

    /**
     * Init Subscription information
     */
    initSubscription() {
      const { subscription, shippingInformation } = this.data
      this.subscription = {
        delivery_pattern: this.deliveryPatterns?.find((item) => item.value === subscription.deliveryPattern)?.label,
        delivery_interval: subscription.deliveryInterval,
        next_delivery_date: this.calcFirstDeliveryDate(
          shippingInformation.desiredDeliveryDay,
          shippingInformation.prefecture === this.Constants.HOKKAIDO_ID
        ),
        next_next_delivery_date: subscription.secondDeliveryDate,
        subscription_id: this.data.subscription_id,
      }
    },

    /**
     * Init list cart
     */
    initOrder() {
      const carts = this.$store.state.cart.carts?.filter(
        (item) =>
          this.saleType === Number(item.subscription) &&
          ![item.is_product_sku_valid, item.visible].includes(false) &&
          item.is_out_stock === false
      )

      if (!carts?.length) return

      const items = carts.map((cart) => ({
        id: cart.id,
        name: cart.product_name,
        img: cart.product_image,
        price: cart.subscription ? cart.price03_after_tax : cart.price02_after_tax,
        subtotal: cart.subscription ? cart.product_subtotal_price03_after_tax : cart.product_subtotal_price02_after_tax,
        quantity: cart.quantity,
        colorName: cart.sku_item_parent_name_1,
        sizeName: cart.sku_item_parent_name_2,
        color: cart.sku_set_item_name1,
        size: cart.sku_set_item_name2,
        discountAmount: cart.product_subtotal_discount_amount,
        is_product_sku_valid: cart.is_product_sku_valid,
        product_id: cart.product_id,
        is_out_stock: cart.is_out_stock,
        sale_limit: cart.sale_limit,
        visible: cart.visible,
        nextPrice: cart.subscription ? cart.next_price03_after_tax : null,
        nextSubtotal: cart.subscription ? cart.next_subtotal_price03_after_tax : null,
        nextDiscountAmount: cart.next_subtotal_discount_amount,
      }))

      this.purchase = {
        order_id: this.data.order_id,
        items,
      }

      this.summary = this.isCartPeriodic
        ? this.$store.state.cart.subscriptionCartsSummary
        : this.$store.state.cart.normalCartsSummary
    },

    resetInfoRegister() {
      this.$store.commit('cart/EMPTY_CART')
      if (this.isRequiredLine) {
        this.$store.commit('register/RESET_INFO')
      }
    },
    getUrlQrCode() {
      const customer_id = this.data.customer_id
      const adcd = localStorage.getItem(ADCD)
      const ad_user_id = localStorage.getItem(AD_USER_ID)

      SocialService.toBeFriend({
        redirect_uri: `${this.$store.getters['env/GET_KEY'](ENV_KEY.BASE_URL)}${process.env.VUE_APP_PREFIX}${
          this.$routePage.CALLBACK
        }?redirect_url=https://line.me/R/ti/p/~${this.$store.getters['env/GET_KEY'](
          ENV_KEY.LINE_BOT_ID
        )}&customer_id=${customer_id}${adcd && ad_user_id ? `&adcd=${adcd}&ad_user_id=${ad_user_id}` : ''}`,
      })
        .then(async ({ data }) => {
          if (data) {
            this.lineLinkUrl = data.authorization_url
          }
        })
        .catch((error) => {
          this.handleError(error)
        })
        .finally(() => this.$store.commit('register/RESET_INFO'))
    },
    onLinkLine() {
      window.location.href = this.lineLinkUrl
    },
    async fetchHtmlContent() {
      const resLineContent = await HtmlService.getHtmlContent(this.Constants.SHOP_CONFIG.THANKS_LINE_CONTENT)
      const resContent1 = await HtmlService.getHtmlContent(this.Constants.SHOP_CONFIG.THANKS_CONTENT1)
      const resContent2 = await HtmlService.getHtmlContent(this.Constants.SHOP_CONFIG.THANKS_CONTENT2)
      this.fvLinePart = resLineContent.data
      this.fvPart1 = resContent1.data
      this.fvPart2 = resContent2.data
    },
  },
  metaInfo() {
    return {
      title: this.isCartPeriodic ? this.$t('ec-line005.metaSubscriptionTitle') : this.$t('ec-line005.metaNormalTitle'),
      meta: [
        {
          vmid: 'description',
          name: 'description',
          content: this.isCartPeriodic
            ? this.$t('ec-line005.description', { shop_name: this.shopName })
            : this.$t('ec-line005.normalDescription', { shop_name: this.shopName }),
        },
        {
          vmid: 'keywords',
          name: 'keywords',
          content: this.isCartPeriodic
            ? this.$t('ec-line005.keyword', { shop_name: this.shopName })
            : this.$t('ec-line005.normalKeyword', { shop_name: this.shopName }),
        },
        {
          vmid: 'robots',
          name: 'robots',
          content: this.Constants.META_TAG.NO_INDEX_NO_FOLLOW,
        },
      ],
    }
  },
}
</script>
<style lang="scss">
@import '@/assets/css/pages/ec-line005.scss';
</style>
